import React from 'react';

const EFFECTIVE_DATE = 'May 18, 2026';
const CONTACT_EMAIL = 'info@interiorfolio.com';
const COMPANY_NAME = 'InteriorFolio';

const styles = {
  page: {
    padding: '60px 24px',
    maxWidth: 820,
    margin: '0 auto',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#1a1a2e',
    lineHeight: 1.75,
  },
  header: {
    borderBottom: '2px solid #e8e4df',
    paddingBottom: 24,
    marginBottom: 40,
  },
  title: {
    fontSize: 36,
    fontWeight: 700,
    margin: '0 0 8px',
    color: '#1a1a2e',
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    margin: 0,
  },
  section: {
    marginBottom: 40,
  },
  h2: {
    fontSize: 20,
    fontWeight: 600,
    color: '#1a1a2e',
    marginBottom: 12,
    marginTop: 0,
    paddingBottom: 6,
    borderBottom: '1px solid #f3f0ec',
  },
  p: {
    margin: '0 0 14px',
    fontSize: 15,
    color: '#374151',
  },
  ul: {
    paddingLeft: 22,
    margin: '0 0 14px',
    fontSize: 15,
    color: '#374151',
  },
  li: {
    marginBottom: 6,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,
    marginBottom: 14,
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    background: '#f9f7f5',
    borderBottom: '2px solid #e8e4df',
    fontWeight: 600,
    color: '#4b5563',
  },
  td: {
    padding: '10px 14px',
    borderBottom: '1px solid #f3f0ec',
    verticalAlign: 'top',
    color: '#374151',
  },
  link: {
    color: '#8b6f5e',
    textDecoration: 'none',
  },
  infoBox: {
    background: '#faf8f6',
    border: '1px solid #e8e4df',
    borderRadius: 8,
    padding: '16px 20px',
    marginBottom: 14,
    fontSize: 14,
    color: '#4b5563',
  },
};

export default function PrivacyPolicy() {
  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.subtitle}>Effective date: {EFFECTIVE_DATE}</p>
      </header>

      <div style={styles.section}>
        <p style={styles.p}>
          {COMPANY_NAME} ("we," "us," or "our") provides practice-management software for interior
          design firms. This Privacy Policy explains what personal data we collect, why we collect
          it, who we share it with, and the rights you have over your information.
        </p>
        <p style={styles.p}>
          By creating an account or using {COMPANY_NAME}, you agree to the practices described in
          this policy. If you do not agree, please do not use our services.
        </p>
      </div>

      {/* 1. Data We Collect */}
      <div style={styles.section}>
        <h2 style={styles.h2}>1. Data We Collect</h2>

        <p style={styles.p}><strong>Account & Identity</strong></p>
        <ul style={styles.ul}>
          <li style={styles.li}>Name, email address, and password (managed via AWS Cognito)</li>
          <li style={styles.li}>Company name, logo, address, phone number, and business details</li>
          <li style={styles.li}>Team member names and roles that you add to your account</li>
        </ul>

        <p style={styles.p}><strong>Business & Project Data</strong></p>
        <ul style={styles.ul}>
          <li style={styles.li}>Projects, rooms, proposals, invoices, and purchase orders you create</li>
          <li style={styles.li}>Products, services, vendors, and contacts you manage</li>
          <li style={styles.li}>Time-billing entries, retainers, credits, and financial reports</li>
          <li style={styles.li}>Files and images you upload (stored in AWS S3)</li>
        </ul>

        <p style={styles.p}><strong>Payment Information</strong></p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Subscription billing is processed by{' '}
            <a href="https://stripe.com/privacy" style={styles.link} target="_blank" rel="noreferrer">
              Stripe
            </a>
            . We do not store raw card numbers — Stripe handles PCI compliance on our behalf.
          </li>
          <li style={styles.li}>
            Client payments collected through {COMPANY_NAME} are also processed by Stripe and
            recorded in your account ledger.
          </li>
        </ul>

        <p style={styles.p}><strong>Usage & Technical Data</strong></p>
        <ul style={styles.ul}>
          <li style={styles.li}>Browser type, operating system, IP address, and device identifiers</li>
          <li style={styles.li}>Pages visited, features used, and session timestamps</li>
          <li style={styles.li}>Error logs and crash reports used for debugging</li>
        </ul>

        <p style={styles.p}><strong>Support Communications</strong></p>
        <ul style={styles.ul}>
          <li style={styles.li}>Messages you send through the in-app support chat</li>
          <li style={styles.li}>Emails you exchange with our support team</li>
        </ul>
      </div>

      {/* 2. How We Use Your Data */}
      <div style={styles.section}>
        <h2 style={styles.h2}>2. How We Use Your Data</h2>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Purpose</th>
              <th style={styles.th}>Legal Basis</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Providing and operating the {COMPANY_NAME} platform', 'Contract performance'],
              ['Processing subscription and client payments via Stripe', 'Contract performance'],
              ['Sending transactional emails (proposals, invoices, notifications) via AWS SES', 'Contract performance'],
              ['Syncing data with QuickBooks Online when you enable the integration', 'Consent / Contract performance'],
              ['Improving features and fixing bugs', 'Legitimate interest'],
              ['Responding to support requests', 'Legitimate interest / Contract performance'],
              ['Complying with legal obligations (tax records, fraud prevention)', 'Legal obligation'],
              ['Sending product updates and announcements (opt-out available)', 'Legitimate interest'],
            ].map(([purpose, basis], i) => (
              <tr key={i}>
                <td style={styles.td}>{purpose.replace('{COMPANY_NAME}', COMPANY_NAME)}</td>
                <td style={styles.td}>{basis}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={styles.p}>
          We do not sell your personal data or use it to train AI models.
        </p>
      </div>

      {/* 3. Third-Party Services */}
      <div style={styles.section}>
        <h2 style={styles.h2}>3. Third-Party Services</h2>
        <p style={styles.p}>
          We share data with the following sub-processors only to the extent necessary to deliver
          the service:
        </p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Provider</th>
              <th style={styles.th}>Purpose</th>
              <th style={styles.th}>Privacy Policy</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Amazon Web Services (AWS)', 'Cloud infrastructure, authentication (Cognito), database (DynamoDB), file storage (S3), and transactional email (SES)', 'https://aws.amazon.com/privacy/'],
              ['Stripe', 'Subscription billing and client payment processing', 'https://stripe.com/privacy'],
              ['Intuit / QuickBooks', 'Accounting sync when you connect your QuickBooks account', 'https://www.intuit.com/privacy/statement/'],
            ].map(([provider, purpose, url], i) => (
              <tr key={i}>
                <td style={styles.td}><strong>{provider}</strong></td>
                <td style={styles.td}>{purpose}</td>
                <td style={styles.td}>
                  <a href={url} style={styles.link} target="_blank" rel="noreferrer">
                    View policy
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={styles.p}>
          We may also disclose data to law enforcement or government authorities when required by
          applicable law.
        </p>
      </div>

      {/* 4. Public Share Links */}
      <div style={styles.section}>
        <h2 style={styles.h2}>4. Public Share Links (Proposals & Invoices)</h2>
        <p style={styles.p}>
          When you share a proposal or invoice with a client via a public link, the document is
          accessible to anyone who has that link — no login required. Share links expire after 30
          days by default. Do not share links with unintended recipients. You can revoke a link at
          any time from your account.
        </p>
      </div>

      {/* 5. Data Retention */}
      <div style={styles.section}>
        <h2 style={styles.h2}>5. Data Retention</h2>
        <p style={styles.p}>
          We retain your account data for as long as your subscription is active. If you close your
          account, we will delete or anonymize your personal data within 90 days, except where we
          are required to retain it by law (e.g., financial records for tax purposes, which may be
          kept for up to 7 years).
        </p>
        <p style={styles.p}>
          Uploaded files in AWS S3 are deleted when you delete the associated project or upon
          account closure.
        </p>
      </div>

      {/* 6. Your Rights */}
      <div style={styles.section}>
        <h2 style={styles.h2}>6. Your Privacy Rights</h2>
        <div style={styles.infoBox}>
          Depending on your jurisdiction, you may have rights under the GDPR (EU/UK), CCPA
          (California), or similar laws. We honor these rights regardless of where you are located.
        </div>
        <ul style={styles.ul}>
          <li style={styles.li}><strong>Access</strong> — Request a copy of the data we hold about you.</li>
          <li style={styles.li}><strong>Correction</strong> — Ask us to correct inaccurate data.</li>
          <li style={styles.li}><strong>Deletion</strong> — Request deletion of your account and associated data.</li>
          <li style={styles.li}><strong>Portability</strong> — Export your business data in a structured format.</li>
          <li style={styles.li}><strong>Objection / Restriction</strong> — Object to or restrict certain processing activities.</li>
          <li style={styles.li}><strong>Opt-out of marketing</strong> — Unsubscribe from non-transactional emails at any time.</li>
        </ul>
        <p style={styles.p}>
          To exercise any of these rights, email us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a>. We will
          respond within 30 days.
        </p>
      </div>

      {/* 7. Security */}
      <div style={styles.section}>
        <h2 style={styles.h2}>7. Security</h2>
        <p style={styles.p}>
          We use industry-standard security measures, including TLS encryption in transit, AES-256
          encryption at rest in AWS, and JWT-based authentication. Access to production systems is
          restricted to authorized personnel.
        </p>
        <p style={styles.p}>
          No method of transmission over the internet is 100% secure. If you discover a security
          vulnerability, please report it responsibly to{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a>.
        </p>
      </div>

      {/* 8. Cookies */}
      <div style={styles.section}>
        <h2 style={styles.h2}>8. Cookies & Local Storage</h2>
        <p style={styles.p}>
          We use browser local storage and session cookies to keep you logged in and to remember
          your preferences (e.g., sidebar state). We do not use third-party advertising cookies or
          cross-site tracking pixels.
        </p>
      </div>

      {/* 9. Children */}
      <div style={styles.section}>
        <h2 style={styles.h2}>9. Children's Privacy</h2>
        <p style={styles.p}>
          {COMPANY_NAME} is a business tool intended for adults. We do not knowingly collect
          personal data from anyone under the age of 16. If you believe we have inadvertently done
          so, please contact us and we will delete the information promptly.
        </p>
      </div>

      {/* 10. Changes */}
      <div style={styles.section}>
        <h2 style={styles.h2}>10. Changes to This Policy</h2>
        <p style={styles.p}>
          We may update this Privacy Policy from time to time. When we do, we will revise the
          effective date at the top of this page and, for material changes, notify you by email or
          via an in-app notice. Your continued use of {COMPANY_NAME} after the effective date
          constitutes acceptance of the updated policy.
        </p>
      </div>

      {/* 11. Contact */}
      <div style={styles.section}>
        <h2 style={styles.h2}>11. Contact Us</h2>
        <p style={styles.p}>
          If you have questions about this Privacy Policy or how we handle your data, please
          contact:
        </p>
        <div style={styles.infoBox}>
          <strong>{COMPANY_NAME}</strong><br />
          Email:{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a>
        </div>
        <p style={styles.p}>
          If you are in the EU or UK and believe we have not addressed your concern, you have the
          right to lodge a complaint with your local data protection authority.
        </p>
      </div>
    </div>
  );
}
